import { useCallback, useState } from "react";

const useOTP = () => {
  const [otp, setOTP] = useState(new Array(6).fill(""));
  const [otps, setOTPS] = useState("");

  // Handle input change for OTP
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const value = e.target.value;

      // Allow only numeric input and limit to a single character
      if (isNaN(Number(value)) || value.length > 1) {
        return;
      }

      // Create a copy of the current OTP array
      const newOtp = [...otp];

      // Update the specific index with the new value
      if (newOtp[index] !== value) {
        newOtp[index] = value;
        setOTP(newOtp);
        setOTPS(newOtp.join(""));

        // Move to the next input only if the current one is filled
        if (value && index < otp.length - 1) {
          (e.target.nextSibling as HTMLInputElement)?.focus();
        }
      }
    },
    [otp]
  );

  // Handle backspace for simultaneous delete
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
      if (e.key === "Backspace" && !otp[index] && index > 0) {
        const previousInput = e.currentTarget
          .previousElementSibling as HTMLInputElement;
        previousInput?.focus();
      }
    },
    [otp]
  );

  // Handle paste event for OTP
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
      const pastedData = e.clipboardData.getData("Text");
      const numbers = pastedData
        .split("")
        .filter((char) => !isNaN(Number(char)))
        .slice(0, 6);

      if (numbers.length > 0) {
        const newOtp = [...otp];

        numbers.forEach((num, i) => {
          if (index + i < newOtp.length) {
            newOtp[index + i] = num;
          }
        });

        setOTP(newOtp);
        setOTPS(newOtp.join(""));

        // Move focus to the next empty input field after pasting
        const nextEmptyIndex = newOtp.findIndex((num) => num === "");
        if (nextEmptyIndex !== -1) {
          (
            e.currentTarget.parentElement?.children[
              nextEmptyIndex
            ] as HTMLInputElement
          )?.focus();
        }
      }
    },
    [otp]
  );

  return {
    otp,
    otps,
    setOTP,
    setOTPS,
    handleChange,
    handleKeyDown,
    handlePaste,
  };
};

export default useOTP;
